import {
  Field,
  FieldError,
  FieldLabel,
  Input,
  Textarea,
} from "@/components/ui";
import { Button } from "@/components/ui/Button";
import { RadioGroup } from "@/components/ui/Radio";
import type { Patient as PatientResource } from "@curai/js-fhir-tools";
import { ServiceRequest } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import i18next from "i18next";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { useSubmitTriage } from "../../network/useSubmitTriage";

const PatientMessages = {
  [ServiceRequest.TRIAGE_ACTIVITY_ACTION.values.CALL_PT]:
    "Hi {{name}}! Your test results are in. You should have received a phone call from a Curai Health licensed clinician to discuss your results. If not, I’d like to for you to discuss them with a licensed clinician when you get this message.",
  [ServiceRequest.TRIAGE_ACTIVITY_ACTION.values.MSG_PT]:
    "Hi {{name}}! Your test results are in. I’d like for you to discuss them with a Curai Health licensed clinician when you get this message.",
  [ServiceRequest.TRIAGE_ACTIVITY_ACTION.values.DO_NOT_CONTACT]:
    "Hi {{name}}! Your Curai Health licensed clinician has reviewed your labs and your labs are normal.",
};

const resultsReviewFormSchema = z.object({
  triageLevel: z.enum(
    [
      ServiceRequest.TRIAGE_ACTIVITY_ACTION.values.REQUEST_PCP_MSG,
      ServiceRequest.TRIAGE_ACTIVITY_ACTION.values.REQUEST_CHAT,
      ServiceRequest.TRIAGE_ACTIVITY_ACTION.values.DO_NOT_CONTACT,
      ServiceRequest.TRIAGE_ACTIVITY_ACTION.values.MSG_PT,
      ServiceRequest.TRIAGE_ACTIVITY_ACTION.values.CALL_PT,
    ],
    {
      required_error: "Triage level is required",
    },
  ),
  triageNote: z.string().min(1, { message: "Triage note is required" }),
  messageForPatient: z
    .string()
    .min(1, { message: "Message for patient is required" }),
});

/**
 * Lab results review form for the legacy (pre-async-labs) triage flow. Patients
 * on the async-labs flow are handled by UniversalClinicResultsReviewForm.
 */
export const LegacyResultsReviewForm = ({
  patientId,
  serviceRequestId,
  patient,
}: {
  patientId: string;
  serviceRequestId: string;
  patient?: PatientResource;
}) => {
  const { mutate: submitTriage, isPending: isSubmitting } = useSubmitTriage({
    patientId,
    serviceRequestId,
  });

  const form = useForm({
    resolver: zodResolver(resultsReviewFormSchema),
    defaultValues: {
      triageLevel: undefined as unknown as z.infer<
        typeof resultsReviewFormSchema
      >["triageLevel"],
      triageNote: "",
      messageForPatient: "",
    },
  });

  const handleSubmit = (values: z.infer<typeof resultsReviewFormSchema>) => {
    submitTriage({
      triageAction: values.triageLevel,
      note: values.triageNote,
      messageForPatient: values.messageForPatient,
    });
  };

  return (
    <form className="grid gap-2" onSubmit={form.handleSubmit(handleSubmit)}>
      <Controller
        control={form.control}
        name="triageLevel"
        render={({ field, fieldState }) => (
          <Field>
            <FieldLabel htmlFor="triageLevel">Triage Level</FieldLabel>
            <RadioGroup
              value={field.value}
              onValueChange={(newValue) => {
                form.setValue(
                  "triageLevel",
                  newValue as z.infer<
                    typeof resultsReviewFormSchema
                  >["triageLevel"],
                );
                form.setValue(
                  "messageForPatient",
                  `${i18next.t(
                    PatientMessages[newValue as keyof typeof PatientMessages],
                    {
                      name: patient?.firstName,
                    },
                  )}`,
                );
              }}
              options={Object.values(
                ServiceRequest.TRIAGE_ACTIVITY_ACTION.values,
              )
                .map((code) => ({
                  label:
                    ServiceRequest.TRIAGE_ACTIVITY_ACTION_LABELS_SHORT[
                      code as keyof typeof ServiceRequest.TRIAGE_ACTIVITY_ACTION_LABELS_SHORT
                    ],
                  value: code,
                }))
                .filter(
                  (option) =>
                    ![
                      ServiceRequest.TRIAGE_ACTIVITY_ACTION.values
                        .REQUEST_PCP_MSG,
                      ServiceRequest.TRIAGE_ACTIVITY_ACTION.values.REQUEST_CHAT,
                      // @ts-ignore
                    ].includes(option.value),
                )}
            />
            <FieldError errors={[fieldState.error]} />
          </Field>
        )}
      />
      <Controller
        control={form.control}
        name="triageNote"
        render={({ field, fieldState }) => (
          <Field>
            <FieldLabel htmlFor="triageNote">Triage Note</FieldLabel>
            <Input id="triageNote" {...field} />
            <FieldError errors={[fieldState.error]} />
          </Field>
        )}
      />
      <Controller
        control={form.control}
        name="messageForPatient"
        render={({ field, fieldState }) => (
          <Field>
            <FieldLabel htmlFor="messageForPatient">
              Message for Patient
            </FieldLabel>
            <Textarea id="messageForPatient" {...field} />
            <FieldError errors={[fieldState.error]} />
          </Field>
        )}
      />
      <Button
        size="xs"
        type="submit"
        className="w-fit justify-self-end"
        isLoading={isSubmitting}
      >
        Submit Review
      </Button>
    </form>
  );
};
